import HttpClient from "./httpClient";
import { API_URLS } from "../utils/apiUrls";

export type ResearchReportJobStatus = "queued" | "running" | "completed" | "failed";

export interface ResearchReportJob {
  job_id: number;
  ai_thread_id: number;
  customer_id: number | null;
  requested_by: string | null;
  request_text: string;
  report_title: string | null;
  status: ResearchReportJobStatus;
  progress_percent: number;
  report_url: string | null;
  gcs_url: string | null;
  error_message: string | null;
  client_error_message: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface ResearchReportAttachmentOption {
  job_id: number;
  report_title: string | null;
  completed_at: string | null;
}

export interface ResearchReportJobsQuery {
  start_date?: string;
  end_date?: string;
  requested_by?: string;
  report_title?: string;
  status?: ResearchReportJobStatus | "";
  ai_thread_id?: string;
  job_id?: string;
  limit?: number;
  offset?: number;
}

interface ApiResponse<T> {
  status: "success" | "error";
  data: T;
  timestamp: string;
}

interface ResearchReportJobsData {
  jobs: ResearchReportJob[];
  total: number;
  limit: number;
  offset: number;
}

interface RequestedByOptionsData {
  requested_by: string[];
}

interface ResearchReportAttachmentOptionsData {
  jobs: ResearchReportAttachmentOption[];
}

class ResearchReportJobsApi extends HttpClient {
  public listJobs = async (
    query: ResearchReportJobsQuery = {}
  ): Promise<ResearchReportJobsData> => {
    const response: ApiResponse<ResearchReportJobsData> = await this.get(
      API_URLS.GET_RESEARCH_REPORT_JOBS,
      {},
      query
    );
    return response.data;
  };

  public listRequestedByOptions = async (): Promise<string[]> => {
    const response: ApiResponse<RequestedByOptionsData> = await this.get(
      API_URLS.GET_RESEARCH_REPORT_JOBS_REQUESTED_BY
    );
    return Array.isArray(response.data?.requested_by) ? response.data.requested_by : [];
  };

  public listAttachmentOptions = async (limit = 200): Promise<ResearchReportAttachmentOption[]> => {
    const response: ApiResponse<ResearchReportAttachmentOptionsData> = await this.get(
      "/api/research-report-jobs/attachments",
      {},
      { limit }
    );
    return Array.isArray(response.data?.jobs) ? response.data.jobs : [];
  };

  public getReportHtml = async ({ jobId }: { jobId: string }): Promise<string> => {
    const response = await this.get(
      `/api/research-report-jobs/${encodeURIComponent(jobId)}/content`
    );
    return String(response || "");
  };

  public downloadReportXlsx = async ({
    jobId,
    title,
  }: {
    jobId: string;
    title?: string | null;
  }): Promise<Blob> => {
    return this.getBlob(`/api/research-report-jobs/${encodeURIComponent(jobId)}/download/xlsx`, {
      ...(title ? { title } : {}),
    });
  };
}

const researchReportJobsApi = new ResearchReportJobsApi();
export default researchReportJobsApi;
